import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Message from "../../components/Message";
import Loader from "../../components/Loader";
import {
  useUpdateProductCreatorMutation,
  useGetProductDetailsForEditQuery,
  useUploadFilesMutation,
} from "../../slices/productsApiSlice.js";
import { useGetUserCreatorQuery } from "../../slices/usersApiSlice.js";
import { searchKeywords } from "../../constants.js";
import { availableLanguages } from "../../constants.js";
import { validateCarouselImage, validateDocumentFile } from "../../utils/fileValidators";
import { getUploadErrorMessage } from "../../utils/uploadToastMessages";
import { FaCloudUploadAlt } from "react-icons/fa";

const getDraftKey = (productId) => `creatorProductDraft:${productId}`;

const parseDraft = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

const toStringArray = (value) =>
  Array.isArray(value) ? value.filter((v) => typeof v === "string" && v.trim() !== "") : [];

function CreatorProductEditScreen() {
  const { id: productId } = useParams();
  const draftKey = getDraftKey(productId);
  const hasHydratedFromSource = useRef(false);
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  const { data: creatorData } = useGetUserCreatorQuery(userInfo._id);
  const { data: product, isLoading, error } = useGetProductDetailsForEditQuery(productId);

  const [updateProduct, { isLoading: loadingUpdate }] = useUpdateProductCreatorMutation();
  const [uploadFiles] = useUploadFilesMutation();

  const [name, setName] = useState("placeholder");
  const [beforePrice, setBeforePrice] = useState(0);
  const [price, setPrice] = useState(0);
  const [isFree, setIsFree] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [files, setFiles] = useState([""]);
  const [images, setImages] = useState([""]);
  const [category, setCategory] = useState([""]);
  const [languages, setLanguages] = useState([]);
  const [description, setDescription] = useState("placeholder");
  const [longDescription, setLongDescription] = useState("placeholder");
  const [loadingImageUpload, setLoadingImageUpload] = useState(false);
  const [loadingFileUpload, setLoadingFileUpload] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredResults, setFilteredResults] = useState([]);

  const handleSearch = (term) => {
    const results = [];
    setSearchTerm(term);

    if (term.trim() !== "") {
      for (const topLevel in searchKeywords) {
        const secondLevelObj = searchKeywords[topLevel];
        for (const secondLevel in secondLevelObj) {
          if (secondLevel.toLowerCase().includes(term.toLowerCase())) {
            results.push({
              topLevel,
              level: "second",
              value: secondLevel,
            });
          }

          secondLevelObj[secondLevel].forEach((child) => {
            if (child.toLowerCase().includes(term.toLowerCase())) {
              results.push({
                topLevel,
                level: "third",
                parent: secondLevel,
                value: child,
              });
            }
          });
        }
      }
    }

    setFilteredResults(results.slice(0, 3));
  };

  const addCategory = (entry) => {
    if (category.length < 10 && !category.includes(entry)) {
      setCategory((prev) => [...prev, entry]);
    } else {
      toast.error("You have reached the keyword limit of 10, remove some to add others!");
    }
    setSearchTerm("");
    setFilteredResults([]);
  };

  const removeCategory = (entry) => {
    setCategory((prev) => prev.filter((cat) => cat !== entry));
  };

  async function submitHandler(e) {
    e.preventDefault();
    const numericPrice = Number(price);

    if (
      !name ||
      (!isFree && (!numericPrice || numericPrice <= 0)) ||
      images.length === 0 ||
      !category ||
      category.length === 0 ||
      !description ||
      files.length === 0
    ) {
      if (!isFree && numericPrice <= 0) {
        toast.error("Price must be greater than 0");
      } else if (!category || category.length === 0) {
        toast.error("Please select at least one Keyword");
      } else {
        toast.error("Please fill in all required fields");
      }
      return;
    }

    const imagePaths = Array.isArray(images) ? images : [images];
    const filePaths = Array.isArray(files) ? files : [files];

    const updatedProduct = {
      productId,
      name,
      price: isFree ? 0 : numericPrice,
      isFree,
      isVisible,
      currency: selectedCurrency,
      files: filePaths,
      beforePrice,
      images: imagePaths,
      category,
      description,
      longDescription,
      ownerName: creatorData?.name || userInfo?.name || "",
      ownerImage: creatorData?.profileImage || userInfo?.profileImage || "",
      ownerUrl: `user/creator/${creatorData?.tag || userInfo?.tag || ""}`,
      languages,
    };

    try {
      const result = await updateProduct(updatedProduct);
      if (result.error) {
        toast.error(result.error.data?.message || "Failed to update product");
      } else {
        localStorage.removeItem(draftKey);
        toast.success("Product updated successfully");
        navigate("/profile/creator");
      }
    } catch (err) {
      toast.error("An unexpected error occurred");
      console.error(err);
    }
  }

  useEffect(() => {
    if (!product) return;

    setName(product.name || "");
    setBeforePrice(Number(product.beforePrice || 0));
    setPrice(product.priceInput || product.price || 0);
    setIsFree(Boolean(product.isFree));
    setIsVisible(Boolean(product.isVisible));
    setSelectedCurrency(product.currencyInput || "USD");
    setFiles(toStringArray(product.files));
    setImages(toStringArray(product.images));
    setCategory(toStringArray(product.category));
    setDescription(product.description || "");
    setLongDescription(product.longDescription || "");
    setLanguages(Array.isArray(product.languages) ? product.languages : ["English (US)"]);

    const draft = parseDraft(localStorage.getItem(draftKey));
    if (draft) {
      if (typeof draft.name === "string") setName(draft.name);
      if (typeof draft.beforePrice === "number") setBeforePrice(draft.beforePrice);
      if (typeof draft.price !== "undefined") setPrice(draft.price);
      if (typeof draft.isFree === "boolean") setIsFree(draft.isFree);
      if (typeof draft.isVisible === "boolean") setIsVisible(draft.isVisible);
      if (typeof draft.selectedCurrency === "string") setSelectedCurrency(draft.selectedCurrency);
      if (typeof draft.description === "string") setDescription(draft.description);
      if (typeof draft.longDescription === "string") setLongDescription(draft.longDescription);
      setFiles(toStringArray(draft.files));
      setImages(toStringArray(draft.images));
      setCategory(toStringArray(draft.category));
      if (Array.isArray(draft.languages)) setLanguages(draft.languages);
    }

    hasHydratedFromSource.current = true;
  }, [product, draftKey]);

  useEffect(() => {
    if (!hasHydratedFromSource.current) return;

    const draftPayload = {
      name,
      beforePrice: Number(beforePrice || 0),
      price,
      isFree: Boolean(isFree),
      isVisible: Boolean(isVisible),
      selectedCurrency,
      files: toStringArray(files),
      images: toStringArray(images),
      category: toStringArray(category),
      languages: Array.isArray(languages) ? languages : [],
      description,
      longDescription,
    };

    localStorage.setItem(draftKey, JSON.stringify(draftPayload));
  }, [
    draftKey,
    name,
    beforePrice,
    price,
    isFree,
    isVisible,
    selectedCurrency,
    files,
    images,
    category,
    languages,
    description,
    longDescription,
  ]);

  async function uploadFileHandler(e, fieldName) {
    const inputEl = e.target;
    const formData = new FormData();
    const filesArray = Array.from(inputEl.files || []);
    filesArray.forEach((file) => {
      formData.append(fieldName, file);
    });

    try {
      for (const file of filesArray) {
        if (fieldName === "image") await validateCarouselImage(file);
        else if (fieldName === "file") validateDocumentFile(file);
      }

      if (fieldName === "image") setLoadingImageUpload(true);
      if (fieldName === "file") setLoadingFileUpload(true);

      const res = await uploadFiles(formData).unwrap();
      toast.success("Upload successful");

      if (fieldName === "image") {
        setImages((prevImages) => [...prevImages, ...res.paths.image]);
      } else if (fieldName === "file") {
        setFiles((prevFiles) => [...prevFiles, ...res.paths.file]);
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(getUploadErrorMessage(err, "File upload failed"));
    } finally {
      if (fieldName === "image") setLoadingImageUpload(false);
      if (fieldName === "file") setLoadingFileUpload(false);
      if (inputEl) inputEl.value = "";
    }
  }

  const removeImage = (index) => {
    try {
      const updatedImages = [...images];
      updatedImages.splice(index, 1);
      setImages(updatedImages);
      toast.success("Image removed successfully");
    } catch (_) {
      toast.error("Failed to remove image");
    }
  };

  const removeFile = (index) => {
    try {
      const updatedFiles = [...files];
      updatedFiles.splice(index, 1);
      setFiles(updatedFiles);
      toast.success("File removed successfully");
    } catch (_) {
      toast.error("Failed to remove file");
    }
  };

  const imagePanelTone = loadingImageUpload
    ? "border-sky-300 bg-sky-50"
    : images.length > 0
      ? "border-emerald-300 bg-emerald-50"
      : "border-slate-300 bg-slate-50";
  const filePanelTone = loadingFileUpload
    ? "border-sky-300 bg-sky-50"
    : files.length > 0
      ? "border-emerald-300 bg-emerald-50"
      : "border-slate-300 bg-slate-50";
  const imageStatusText = loadingImageUpload
    ? "Uploading images..."
    : images.length > 0
      ? `${images.length} image${images.length === 1 ? "" : "s"} ready for display`
      : "No product images uploaded yet";
  const fileStatusText = loadingFileUpload
    ? "Uploading files..."
    : files.length > 0
      ? `${files.length} downloadable file${files.length === 1 ? "" : "s"} attached`
      : "No product files uploaded yet";
  const statusPillClass = (isBusy, hasItems) =>
    `inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
      isBusy
        ? "bg-sky-100 text-sky-700"
        : hasItems
          ? "bg-emerald-100 text-emerald-700"
          : "bg-slate-200 text-slate-600"
    }`;

  return (
    <div className="space-y-4">
      <Link
        to="/profile/creator/"
        className="inline-flex rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
      >
        Go Back
      </Link>

      <div className="mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h1 className="mb-4 text-3xl font-semibold text-slate-900">Edit Product</h1>
        {loadingUpdate && <Loader />}
        {isLoading ? (
          <Loader />
        ) : error ? (
          <Message variant="danger">
            {error?.data?.message || error?.error || "Failed to load product"}
          </Message>
        ) : (
          <form onSubmit={submitHandler} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Name</label>
              <input
                type="text"
                placeholder="Enter name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Price</label>
              <input
                type="number"
                placeholder="Enter price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                disabled={isFree}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:bg-slate-100"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={isFree}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsFree(checked);
                  if (checked) setPrice(0);
                }}
                className="h-4 w-4 accent-sky-600"
              />
              Set this product as Free
            </label>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={isVisible}
                onChange={(e) => setIsVisible(e.target.checked)}
                className="h-4 w-4 accent-sky-600"
              />
              Product visible in marketplace
            </label>
            <p className="text-xs leading-5 text-slate-500">
              Hidden products stay saved in your dashboard but do not appear publicly in the
              marketplace until you make them visible.
            </p>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Currency</label>
              <select
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              >
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="MAD">MAD - Moroccan Dirham</option>
                <option value="JPY">JPY - Japanese Yen</option>
              </select>
            </div>

            <div>
              <div className={`rounded-2xl border border-dashed p-4 transition ${imagePanelTone}`}>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Upload Images</label>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className={statusPillClass(loadingImageUpload, images.length > 0)}>
                    {imageStatusText}
                  </span>
                </div>
                <label className="flex cursor-pointer items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm transition hover:border-sky-300 hover:bg-sky-50">
                  <span className="flex items-center gap-2 font-semibold text-slate-800">
                    <FaCloudUploadAlt className="text-sky-600" />
                    Choose product images
                  </span>
                  <span className="rounded-full bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white">
                    Browse
                  </span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => uploadFileHandler(e, "image")}
                    className="sr-only"
                  />
                </label>
                <p className="mt-2 text-xs leading-5 text-slate-500">
                  You can upload multiple images to showcase the product from different angles or
                  pages. For the cleanest display, use clear images around 1200 x 1200 pixels or
                  larger in a square or near-square format.
                </p>
              </div>
              <div className="mt-3 flex flex-wrap gap-3">
                {images.length > 0 &&
                  images.map((img, index) => (
                    <div key={`${img}-${index}`} className="relative">
                      <img
                        src={img}
                        alt={`Uploaded ${index}`}
                        className="h-24 w-24 rounded-lg border border-slate-200 object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white"
                        aria-label="Remove image"
                      >
                        x
                      </button>
                    </div>
                  ))}
              </div>
              {loadingImageUpload && <Loader />}
            </div>

            <div>
              <div className={`rounded-2xl border border-dashed p-4 transition ${filePanelTone}`}>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Upload Files</label>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className={statusPillClass(loadingFileUpload, files.length > 0)}>
                    {fileStatusText}
                  </span>
                </div>
                <label className="flex cursor-pointer items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm transition hover:border-sky-300 hover:bg-sky-50">
                  <span className="flex items-center gap-2 font-semibold text-slate-800">
                    <FaCloudUploadAlt className="text-sky-600" />
                    Choose downloadable files
                  </span>
                  <span className="rounded-full bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white">
                    Browse
                  </span>
                  <input
                    type="file"
                    multiple
                    accept=".zip,.rar,.7z,.pdf,.docx,.odf,.pptx,.odp"
                    onChange={(e) => uploadFileHandler(e, "file")}
                    className="sr-only"
                  />
                </label>
                <p className="mt-2 text-xs leading-5 text-slate-500">
                  Upload the resource files buyers will receive. Allowed formats are validated and
                  every upload is checked before it becomes available in the product.
                </p>
              </div>
              <ul className="mt-3 space-y-2">
                {files.length > 0 &&
                  files.map((file, index) => (
                    <li
                      key={`${file}-${index}`}
                      className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    >
                      <span className="truncate">{file.split("/").pop()}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white hover:bg-red-700"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
              </ul>
              {loadingFileUpload && <Loader />}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Languages Available</label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {availableLanguages.map((lang) => (
                  <label key={lang} className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={languages.includes(lang)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setLanguages((prev) => [...prev, lang]);
                        } else {
                          setLanguages((prev) => prev.filter((l) => l !== lang));
                        }
                      }}
                      className="h-4 w-4 accent-sky-600"
                    />
                    {lang}
                  </label>
                ))}
              </div>
            </div>

            <div className="relative">
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Keywords</label>
              <p className="mb-2 text-xs leading-5 text-slate-500">
                Keywords help buyers find the product through search and category browsing. Choose
                the terms that best describe the subject, goal, and resource focus.
              </p>
              <div className="mb-2 flex flex-wrap gap-2">
                {category.map((entry, index) => (
                  <span
                    key={`${entry}-${index}`}
                    className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-800"
                  >
                    {entry}
                    <button
                      type="button"
                      onClick={() => removeCategory(entry)}
                      className="rounded-full bg-sky-700 px-1.5 py-0.5 text-[10px] text-white hover:bg-sky-800"
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                placeholder="Type to search for keywords..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />

              {filteredResults.length > 0 && (
                <div className="absolute z-20 mt-2 w-full rounded-xl border border-slate-200 bg-white p-1 shadow-xl">
                  {filteredResults.map((result, index) => (
                    <button
                      key={`${result.value}-${index}`}
                      type="button"
                      onClick={() => addCategory(result.value)}
                      className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100"
                    >
                      <span className="font-medium text-slate-900">{result.value}</span>
                      <span className="ml-1 text-xs text-slate-500">
                        {result.level === "second"
                          ? `(${result.topLevel})`
                          : `(${result.topLevel} -> ${result.parent})`}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Description</label>
              <textarea
                rows={4}
                placeholder="Enter description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                Detailed Description
              </label>
              <textarea
                rows={8}
                placeholder="Enter detailed description"
                value={longDescription}
                onChange={(e) => setLongDescription(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </div>

            <button
              type="submit"
              className="rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700"
            >
              Update
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default CreatorProductEditScreen;

